import { createOpenAI } from "@ai-sdk/openai";
import { type ModelMessage, generateText, tool, stepCountIs, hasToolCall, type TypedToolResult } from "ai";
import { AlarmSchedule } from "@visioni/a3s-alarm-schedules";
import { extractJsonFromText } from "./extract-json";
import type { AlarmConfiguration } from "@visioni/a3s-alarm-schedules/schema";
import { type SkillType, getSkill } from "./skills/skills-registry";
import { type Camera } from "./domain/camera";
import { type Site } from "./domain/site";
import { AISkill, type PublicHoliday, type WeatherWarning } from "./types";
import { z } from "zod/v4";

const DEFAULT_MODEL = process.env["OPENAI_MODEL"] ?? "gpt-5-chat-latest";

export type ScheduleHandler = (schedule: AlarmSchedule) => AlarmSchedule;
export type ClarificationHandler = (prompt: string) => string;

export interface SchedulerAgentConfig {
  openAIApiKey?: string;
  model?: string;
  maxTurns?: number;
  onCompletion?: CompletionHandler;
  onError?: (error: Error) => void;
}

type CompletionHandler = (result: { messages: ModelMessage[] }) => void;

type ChatMessage = {
  role: "system" | "user" | "assistant";
  content: string;
};

// todo: use real types when defined
export interface SchedulerAgentContext {
  site?: Site;
  camera?: Camera;
  allCameras?: Camera[];
  currentSchedule?: AlarmSchedule;
  currentAlarmConfiguration?: AlarmConfiguration;
  holidays?: PublicHoliday[];
  weatherWarnings?: WeatherWarning[];
}

export interface SchedulerAgentInput {
  userQuery: string;
  history?: ChatMessage[];
  context: SchedulerAgentContext;
  skill: SkillType;
}

export type SchedulerAgentResult = { messages: ModelMessage[] };

export interface SchedulerAgentClient {
  run(input: SchedulerAgentInput): Promise<SchedulerAgentResult>;
}

export function createSchedulerAgent(config: SchedulerAgentConfig): SchedulerAgentClient {
  if (!config.openAIApiKey) {
    throw new Error("openAIApiKey must be provided");
  }
  const openai = createOpenAI({ apiKey: config.openAIApiKey ?? "" });

  const systemPromptWithContext = (skillType: SkillType, context: SchedulerAgentContext) => {
    const skill = getSkill(skillType);
    const contextParts = [
      `- Current Date/Time (UTC): ${new Date().toISOString()}`,
      ...(context.camera ? [`- Current Selected Camera: ${JSON.stringify(context.camera)}`] : []),
      ...(context.allCameras ? [`- All Cameras: ${JSON.stringify(context.allCameras)}`] : []),
      ...(context.site ? [`- Site: ${JSON.stringify(context.site)}`] : []),
      ...(context.currentSchedule ? [`- Current Schedule: ${JSON.stringify(context.currentSchedule)}`] : []),
      ...(context.currentAlarmConfiguration ?
        [`- Current Alarm: ${JSON.stringify(context.currentAlarmConfiguration)}`]
      : []),
    ];

    if (context.holidays && context.holidays.length > 0) {
      contextParts.push(`- Upcoming Public Holidays: ${JSON.stringify(context.holidays)}`);
    }

    if (context.weatherWarnings && context.weatherWarnings.length > 0) {
      contextParts.push(`- Severe Weather Warnings: ${JSON.stringify(context.weatherWarnings)}`);
    }

    return `${skill.systemPrompt}
    You have the following context about the user:
    ${contextParts.join("\n    ")}
    `;
  };

  const fewShotExamples = (skill: AISkill) => {
    if (skill.fewShotExamples.length === 0) {
      return "";
    }

    const formattedExamples = skill.fewShotExamples
      .map((examplePair, index) => {
        const exampleMessages = examplePair
          .map((msg) => `${msg.role}: ${typeof msg.content === "string" ? msg.content : JSON.stringify(msg.content)}`)
          .join("\n");
        return `Example ${String(index + 1)}:\n${exampleMessages}`;
      })
      .join("\n\n");

    return `\n\nHere are some examples of how to use this skill:\n\n${formattedExamples}\n\nThis marks the end of the examples.`;
  };

  return {
    async run(input: SchedulerAgentInput): Promise<{ messages: ModelMessage[] }> {
      const { history, context, skill: skillType } = input;
      const skill = getSkill(skillType);
      const systemPrompt = systemPromptWithContext(skillType, context) + fewShotExamples(skill);

      const messages: ModelMessage[] = [
        { role: "system" as const, content: systemPrompt },
        ...(history ?? []),
        { role: "user" as const, content: input.userQuery },
      ];

      const model = openai(config.model ?? DEFAULT_MODEL);

      const MAX_STEPS = config.maxTurns ?? 3;

      const tools = {
        clarify: tool({
          description: "Ask the user exactly one concise clarifying question. Favour brevity and conciseness.",
          inputSchema: z.object({ question: z.string().min(1).max(200) }),
          execute: ({ question }) => {
            return { answer: question };
          },
        }),
        validateSchedule: tool({
          description: "Validate if your schedule JSON is valid",
          inputSchema: z.object({
            schedule: z.json(),
          }),
          execute: ({ schedule }) => {
            try {
              const newSchedule = AlarmSchedule.parse(schedule);
              return { validation: true, schedule: newSchedule };
            } catch {
              return { validation: false, schedule: null };
            }
          },
        }),
      };

      const generateConfig = {
        model,
        messages,
        tools,
        stopWhen: [hasToolCall("clarify"), stepCountIs(MAX_STEPS)],
        onStepFinish: ({ toolResults }: { toolResults: TypedToolResult<typeof tools>[] }) => {
          if (toolResults.length > 0) {
            console.log("onStepFinish Tool results:");
            console.dir(toolResults, { depth: null });
          }
        },
      };

      try {
        const { text } = await generateText(generateConfig);

        const jsonStr =
          text && (text.startsWith("{") || text.startsWith("[")) ? text
          : text ? extractJsonFromText(text)
          : null;

        if (jsonStr) {
          let parsedJson: unknown;
          try {
            parsedJson = JSON.parse(jsonStr);
          } catch (jsonError) {
            throw new Error(`Failed to parse JSON: ${String(jsonError)}`);
          }
          // Validate output based on skill type
          if (skillType === "schedule_manager" || skillType === "schedule_exception_suggester") {
            // Validate as AlarmSchedule for schedule-related skills
            const schedule = AlarmSchedule.parse(parsedJson);
            messages.push({
              role: "assistant" as const,
              content: JSON.stringify(schedule),
            });
          } else {
            // For other skills (including alarm_manager), pass through the JSON as-is
            // TODO: validate with Zod when alarm dto schemas are defined
            messages.push({
              role: "assistant" as const,
              content: JSON.stringify(parsedJson),
            });
          }
        } else if (text) {
          messages.push({
            role: "assistant" as const,
            content: text,
          });
        }

        if (process.env["AI_AGENT_DEBUG"]) {
          console.log({
            title: "generateText messages",
            messages,
          });
        }

        const withoutSystemPrompt = messages.filter((m) => m.role !== "system");

        config.onCompletion?.({ messages: withoutSystemPrompt });

        return { messages: withoutSystemPrompt };
      } catch (error) {
        console.error({
          title: "generateText error",
          error,
        });
        throw error;
      }
    },
  };
}
